import type { AiInsightResponse, InsightBrief } from './types';
import { normaliseIngredientName } from './ingredientTaxonomy';

const HEADLINE_MAX = 120;

export interface GuardResult {
  ai: AiInsightResponse;
  headlineReplaced: boolean;
  summarySentencesDropped: number;
  /** Disallowed brief names that were found in the original headline or summary. */
  disallowed: string[];
}

const norm = (s: string) => normaliseIngredientName(s);
const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Every food or group name the brief shows the model. */
export function briefNames(brief: InsightBrief): string[] {
  const names = new Set<string>();
  const add = (n: string | undefined) => {
    if (n && norm(n)) names.add(norm(n));
  };
  brief.ingredientStats.forEach((s) => add(s.name));
  brief.safeIngredients.forEach((s) => add(s.name));
  brief.ruledOut.forEach((r) => add(r.name));
  brief.componentStats.forEach((c) => add(c.name));
  (brief.patternCandidates ?? []).forEach((c) => c.items.forEach(add));
  return [...names];
}

/**
 * Brief names mentioned in text (case-insensitive, whole words). Longer names win: "soy sauce" is
 * matched before "soy", and the span it covers cannot match again.
 */
export function mentionedNames(text: string, names: string[]): string[] {
  let t = text.toLowerCase().replace(/[\s-]+/g, ' ');
  const found: string[] = [];
  for (const n of [...names].sort((a, b) => b.length - a.length)) {
    const re = new RegExp(`(?<![a-z0-9])${escape(n)}(?![a-z0-9])`, 'g');
    if (re.test(t)) {
      found.push(n);
      t = t.replace(re, (m) => ' '.repeat(m.length));
    }
  }
  return found;
}

function firstSentence(text: string): string {
  const m = text.trim().match(/^.*?[.!?](?=\s|$)/);
  return (m ? m[0] : text.trim()).trim();
}

function trimTo(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const sp = cut.lastIndexOf(' ');
  return `${(sp > 40 ? cut.slice(0, sp) : cut).replace(/[\s,;:.-]+$/, '')}…`;
}

/**
 * Headline/summary guard. Allowed names = items of the AI's findings + engineLeadReview "keep" names.
 * A headline that names any other brief name is replaced by "<first finding title>: <first sentence of
 * its explanation>" (120 chars max; falls back to the title, then to a generic line, if those also name
 * a disallowed item). Summary sentences naming a disallowed item are dropped; if none remain, a
 * template sentence about the first finding is used. Runs before verification (findings are unverified).
 */
export function guardHeadlineSummary(ai: AiInsightResponse, brief: InsightBrief): GuardResult {
  const allowed = new Set<string>(
    [
      ...ai.findings.flatMap((f) => f.items.map(norm)),
      ...ai.engineLeadReview.filter((l) => l.verdict === 'keep').map((l) => norm(l.name)),
    ].filter(Boolean),
  );
  // Allowed names join the match list so a longer allowed name ("soy lecithin") masks a shorter
  // disallowed one ("soy") inside it.
  const names = [...new Set([...briefNames(brief), ...allowed])];
  const bad = (text: string) => mentionedNames(text, names).filter((n) => !allowed.has(n));
  const disallowed = new Set<string>();

  let headline = ai.headline;
  let headlineReplaced = false;
  const hBad = bad(headline);
  if (hBad.length) {
    hBad.forEach((n) => disallowed.add(n));
    headlineReplaced = true;
    const f = ai.findings[0];
    const options = f
      ? [trimTo(`${f.title.trim()}: ${firstSentence(f.explanation)}`, HEADLINE_MAX), trimTo(f.title.trim(), HEADLINE_MAX)]
      : [];
    headline = options.find((o) => o && bad(o).length === 0) ?? 'Your weekly gut report is ready.';
  }

  const sentences = ai.summary.trim().split(/(?<=[.!?])\s+/).filter((x) => x.trim());
  const kept = sentences.filter((x) => {
    const b = bad(x);
    b.forEach((n) => disallowed.add(n));
    return b.length === 0;
  });
  let summary = ai.summary;
  const dropped = sentences.length - kept.length;
  if (dropped > 0) {
    if (kept.length) summary = kept.join(' ');
    else {
      const f = ai.findings[0];
      const t = f ? `The strongest finding this week: ${f.title.trim().replace(/[.!?]+$/, '')}.` : '';
      summary = t && bad(t).length === 0 ? t : 'See the findings below for this week’s patterns.';
    }
  }

  return {
    ai: { ...ai, headline, summary },
    headlineReplaced,
    summarySentencesDropped: dropped,
    disallowed: [...disallowed].sort(),
  };
}

/** Names a lead "cannot yet be told apart from", read from its engine hint. */
function inseparableFrom(hint: string): string[] {
  return [...hint.matchAll(/cannot yet be told apart from ([^;]+)/g)].map((m) => m[1].trim());
}

const joinNames = (names: string[]) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Apply the engine's firm lead verdicts (brief.leadHints[].firm) to the AI's engineLeadReview:
 * the verdict becomes the engine's and the reason gets an "Engine: " prefix. When the engine
 * changed the verdict, the AI's reason (which argued the other way) is replaced by the engine's.
 * A firm keep on a lead the diary cannot yet separate from another says so. A firm lead the AI
 * left out of the review is added with the engine's verdict and reason.
 * Run before guardHeadlineSummary so allowed names follow the final verdicts.
 */
export function applyFirmVerdicts(ai: AiInsightResponse, brief: InsightBrief): AiInsightResponse {
  const firm = new Map<string, { verdict: 'keep' | 'reject'; name: string; apart: string[] }>();
  for (const h of brief.leadHints ?? []) {
    if (h.firm) firm.set(norm(h.name), { verdict: h.firm, name: h.name, apart: inseparableFrom(h.hint ?? '') });
  }
  if (firm.size === 0) return ai;
  const stats = new Map([...brief.componentStats, ...brief.ingredientStats].map((s) => [norm(s.name), s]));
  const apartNote = (key: string) => {
    const apart = firm.get(key)?.apart ?? [];
    return apart.length ? `it cannot yet be told apart from ${joinNames(apart)}` : '';
  };
  const engineReason = (key: string, verdict: 'keep' | 'reject'): string => {
    if (verdict === 'reject') {
      return 'Engine: rejected as a bystander: it flares no more than usual without the foods it is eaten with.';
    }
    const s = stats.get(key);
    const nums = s ? ` (followed by a flare ${s.timesFollowedByFlare} of ${s.timesEaten} times, lift ${s.lift})` : '';
    const note = apartNote(key);
    return `Engine: kept: high confidence${nums} and not a bystander in any pair${note ? `, but ${note}` : ''}.`;
  };
  const seen = new Set<string>();
  const reviewed = ai.engineLeadReview.map((l) => {
    const key = norm(l.name);
    seen.add(key);
    const f = firm.get(key);
    if (!f) return l;
    let reason = f.verdict === l.verdict ? `Engine: ${l.reason.replace(/^Engine:\s*/i, '')}` : engineReason(key, f.verdict);
    const note = f.verdict === 'keep' ? apartNote(key) : '';
    if (note && !/told apart/i.test(reason)) reason = `${reason.replace(/[.\s]+$/, '')}; ${note}.`;
    return { ...l, verdict: f.verdict, reason };
  });
  for (const [key, f] of firm) {
    if (!seen.has(key)) reviewed.push({ name: f.name, verdict: f.verdict, reason: engineReason(key, f.verdict) });
  }
  return { ...ai, engineLeadReview: reviewed };
}
